import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { LinkSimple } from '@hivebrite/design-system/Link';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { toast } from '@hivebrite/design-system/Toast';

import { useCommentsContext } from 'hb-react/frontoffice/apps/CommentsV2/contexts/CommentsContext';
import { makeCommentLink } from 'hb-react/frontoffice/apps/CommentsV2/utils';
import copyToClipboard from 'hb-react/shared/utils/copyToClipboard';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

export const CommentDateLink = ({ comment, createdAt }) => {
  const { parentUrl } = useCommentsContext();
  const [isPressed, setIsPressed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const commentUrl = useMemo(
    () => (parentUrl ? makeCommentLink({ comment, parentUrl }) : ''),
    [comment, parentUrl],
  );

  const handleCopyLink = useCallback(
    (event) => {
      event?.preventDefault();
      // Sync with toast message
      const duration = 4000;

      copyToClipboard(commentUrl, () => {
        toast.success(__('Link copied to clipboard'), { duration });
      });

      setIsPressed(true);
      timerRef.current = setTimeout(setIsPressed, duration, false);
    },
    [commentUrl],
  );

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    [],
  );

  if (!parentUrl) {
    return <CommentDate createdAt={createdAt} />;
  }

  return (
    <Link
      to={commentUrl}
      // @ts-expect-error dunno
      font="metadata-regular-asphalt"
      onClick={handleCopyLink}
    >
      {dayjs(createdAt).fromNow()}{' '}
      {isPressed ? (
        <FontAwesomeIcon height="12px" iconName="check" color="grass" />
      ) : (
        <FontAwesomeIcon height="12px" iconName="copy" color="inherit" />
      )}
      <ScreenReadersContent>
        {s__('Comments|Click to copy link to this comment')}
      </ScreenReadersContent>
    </Link>
  );
};

export const CommentDate = ({ createdAt }) => dayjs(createdAt).fromNow();

const Link = styled(LinkSimple)`
  svg {
    opacity: 0;
    transition:
      opacity 0.2s,
      transform 1s;
  }
  &:hover svg {
    opacity: 1;
  }
`;
